import { getCookie } from './cookies/cookies';
import { CookieKey } from './cookies/cookie-keys.const';

declare const ENVIRONMENT: string;
declare const API_URL: string;
declare const API_URL_V2: string;
declare const API_URL_EU: string;
declare const API_URL_CA: string;
declare const ACCOUNT_URL: string;
declare const ACCOUNT_URL_EU: string;
declare const ACCOUNT_URL_CA: string;
declare const STATIC_HOST_URL: string;
declare const STATIC_HOST_URL_EU: string;
declare const STATIC_HOST_URL_CA: string;
declare const SERVER_URL: string;
declare const SERVER_URL_EU: string;
declare const SERVER_URL_CA: string;
declare const WEBSITE_URL: string;
declare const WEBSITE_URL_EU: string;
declare const WEBSITE_URL_CA: string;
declare const SENDBIRD_APP_ID: string;
declare const SENDBIRD_EU_APP_ID: string;
declare const SENDBIRD_CA_APP_ID: string;
declare const GOOGLE_MAPS_KEY: string;
declare const GOOGLE_MAPS_KEY_EU: string;
declare const GOOGLE_MAPS_KEY_CA: string;
declare const STATSIG_CLIENT_ID: string;
declare const STATSIG_PROXY_URL: string;

export const environment = {
  ENVIRONMENT,
  API_URL,
  API_URL_V2,
  API_URL_EU,
  API_URL_CA,
  ACCOUNT_URL,
  ACCOUNT_URL_EU,
  ACCOUNT_URL_CA,
  STATIC_HOST_URL,
  STATIC_HOST_URL_EU,
  STATIC_HOST_URL_CA,
  SERVER_URL,
  SERVER_URL_EU,
  SERVER_URL_CA,
  WEBSITE_URL,
  WEBSITE_URL_EU,
  WEBSITE_URL_CA,
  SENDBIRD_APP_ID,
  SENDBIRD_EU_APP_ID,
  SENDBIRD_CA_APP_ID,
  GOOGLE_MAPS_KEY,
  GOOGLE_MAPS_KEY_EU,
  GOOGLE_MAPS_KEY_CA,
  STATSIG_CLIENT_ID,
  STATSIG_PROXY_URL,
};

export enum Region {
  US = 'us',
  EU = 'eu',
  CA = 'ca',
}

export enum ENV {
  LOCAL = 'local',
  LOCAL_PREVIEW = 'localPreview',
  PREVIEW = 'preview',
  STAGING = 'staging',
  PRODUCTION = 'production',
}

export function isLocal(): boolean {
  return environment.ENVIRONMENT === ENV.LOCAL;
}

export function isLocalPreview(): boolean {
  return environment.ENVIRONMENT === ENV.LOCAL_PREVIEW;
}

export function isAnyLocal(): boolean {
  return isLocal() || isLocalPreview();
}

export function isPreview(): boolean {
  return environment.ENVIRONMENT === ENV.PREVIEW;
}

export function isStaging(): boolean {
  return environment.ENVIRONMENT === ENV.STAGING;
}

export function isProduction(): boolean {
  return environment.ENVIRONMENT === ENV.PRODUCTION;
}

/**
 * Returns true when the current host belongs to the given region's static-host
 * domain. Matches against the domain minus the leading `app.` segment so any
 * subdomain under the region (e.g. tracking.eu.gomotive.com) is detected.
 */
function isRegionDomain(regionStaticHostUrl: string): boolean {
  try {
    const url = new URL(regionStaticHostUrl);
    let staticHostUrl: any = url.hostname;
    staticHostUrl = staticHostUrl.split('.');
    staticHostUrl.shift(); // remove the 'app.' part of URL
    staticHostUrl = staticHostUrl.join('.');
    return !!staticHostUrl && location.hostname.endsWith(staticHostUrl);
  } catch (err) {
    return false;
  }
}

export function isEuDomain(): boolean {
  return isRegionDomain(environment.STATIC_HOST_URL_EU);
}

export function isCaDomain(): boolean {
  return isRegionDomain(environment.STATIC_HOST_URL_CA);
}

export function getRegion(): Region {
  if (isEuDomain()) return Region.EU;
  if (isCaDomain()) return Region.CA;
  return Region.US;
}

/** Picks the value for the current region from a region-keyed map. */
export function byRegion<T>(map: Record<Region, T>): T {
  return map[getRegion()];
}

/**
 * Returns the AWS/cloud region code for the current region, used to build
 * region-scoped infra hostnames (e.g. `labs-frontend.<code>.<env>.k2labs.org`).
 */
export function getCloudRegionCode(): string {
  return byRegion({
    [Region.US]: 'use1',
    [Region.EU]: 'euc1',
    [Region.CA]: 'cac1',
  });
}

export function getApiUrl(): string {
  return byRegion({
    [Region.US]: environment.API_URL,
    [Region.EU]: environment.API_URL_EU,
    [Region.CA]: environment.API_URL_CA,
  });
}

export function getApiUrlV2(): string {
  return byRegion({
    [Region.US]: environment.API_URL_V2,
    [Region.EU]: environment.API_URL_EU,
    [Region.CA]: environment.API_URL_CA,
  });
}

export function getAccountUrl(): string {
  return byRegion({
    [Region.US]: environment.ACCOUNT_URL,
    [Region.EU]: environment.ACCOUNT_URL_EU,
    [Region.CA]: environment.ACCOUNT_URL_CA,
  });
}

export function getServerUrl(): string {
  return byRegion({
    [Region.US]: environment.SERVER_URL,
    [Region.EU]: environment.SERVER_URL_EU,
    [Region.CA]: environment.SERVER_URL_CA,
  });
}

export function getStaticHostUrl() {
  return byRegion({
    [Region.US]: environment.STATIC_HOST_URL,
    [Region.EU]: environment.STATIC_HOST_URL_EU,
    [Region.CA]: environment.STATIC_HOST_URL_CA,
  });
}

export function getWebsiteUrl() {
  return byRegion({
    [Region.US]: environment.WEBSITE_URL,
    [Region.EU]: environment.WEBSITE_URL_EU,
    [Region.CA]: environment.WEBSITE_URL_CA,
  });
}

export function getSendBirdAppId(): string {
  return byRegion({
    [Region.US]: environment.SENDBIRD_APP_ID,
    [Region.EU]: environment.SENDBIRD_EU_APP_ID,
    [Region.CA]: environment.SENDBIRD_CA_APP_ID,
  });
}

export function getGoogleMapsKey(): string {
  return byRegion({
    [Region.US]: environment.GOOGLE_MAPS_KEY,
    [Region.EU]: environment.GOOGLE_MAPS_KEY_EU,
    [Region.CA]: environment.GOOGLE_MAPS_KEY_CA,
  });
}

/**
 * The region-scoped auth-token cookie key for the current region. CA is a fully
 * separate region with its own cookie (not grouped under North America/`_NA`).
 */
function getRegionAuthTokenKey(): CookieKey {
  return byRegion({
    [Region.US]: CookieKey.AUTH_TOKEN_NA,
    [Region.EU]: CookieKey.AUTH_TOKEN_EU,
    [Region.CA]: CookieKey.AUTH_TOKEN_CA,
  });
}

export function getAuthToken(): string {
  return getCookie(getRegionAuthTokenKey()) || getCookie(CookieKey.AUTH_TOKEN);
}

export function getAuthTokenKey(): CookieKey {
  const regionKey = getRegionAuthTokenKey();
  return getCookie(regionKey) ? regionKey : CookieKey.AUTH_TOKEN;
}

export function getCiamAuthToken() {
  const ciamAuthToken = getCookie(
    byRegion({
      [Region.US]: CookieKey.CIAM_AUTH_TOKEN_NA,
      [Region.EU]: CookieKey.CIAM_AUTH_TOKEN_EU,
      [Region.CA]: CookieKey.CIAM_AUTH_TOKEN_CA,
    }),
  );
  return ciamAuthToken || getCookie(CookieKey.CIAM_AUTH_TOKEN);
}
